/**
 * DirtyForm Stimulus Controller
 *
 * This controller provides dirty form detection for forms using Stimulus.js.
 * It warns users if they attempt to navigate away from a form with unsaved changes.
 *
 * Adapted from the jQuery Plugin: Are-You-Sure (Dirty Form Detection)
 *   https://github.com/codedance/jquery.AreYouSure/
 *
 * Copyright (c) 2012-2014, Chris Dance and PaperCut Software http://www.papercut.com/
 * Dual licensed under the MIT or GPL Version 2 licenses.
 *   http://jquery.org/license
 *
 * Author of original plugin: chris.dance@papercut.com
 * Version: 1.9.0 (original plugin)
 * Date:    13th August 2014 (original plugin)
 *
 * This Stimulus controller is a modern JavaScript adaptation for use in Rails applications
 * and is inspired by the logic and behavior of the original Are-You-Sure jQuery plugin.
 *
 * Usage:
 *   Add data-controller="dirty-form" to your <form> element.
 *   Optionally configure targets and values for custom behavior.
 *   To also catch turbo navigation, include 'shared/dirty_form_modal' partial inside the controller element.
 */

import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="dirty-form"
// only need to declare controller and 'form' target by default
export default class extends Controller {
  static targets = [
    "field",
    "form",
    "message",
    "modal"
  ];

  static values = {
    isDirty: { type: Boolean, default: false },
    message: { type: String, default: 'You have unsaved changes! Are you sure you want to leave this page? All unsaved progress will be lost.' },
    silent: { type: Boolean, default: false },
    addRemoveFieldsMarksDirty: { type: Boolean, default: false },
    fieldSelector: { type: String, default: "input:not([type=submit]):not([type=button]), select, textarea" },
    origFieldCount: { type: Number, default: 0 },
    dirtyAttributes: { type: String, default: "dirty" },
    fieldEvents: { type: Array, default: ["change", "keyup"] },
    destinationUrl: { type: String, default: "" }
  }

  constructor(...args) {
    super(...args);
    this._beforeUnload = this.beforeUnloadHandler.bind(this);
    this._checkForm = this.checkForm.bind(this);
    this._beforeUnloadTurbo = this.beforeUnloadHandlerTurbo.bind(this);
  }

  connect(){
    if (!this.hasFormTarget || this.formTarget.tagName.toLowerCase() !== 'form') {
      return;
    }
    var dirtyFormActions = this.formTarget.getAttribute('data-action') || '';
    dirtyFormActions += " submit->dirty-form#onFormSubmit reset->dirty-form#onFormReset dirtyForm.checkForm->dirty-form#checkForm dirtyForm.rescan->dirty-form#rescan dirtyForm.markDirty->dirty-form#markFormDirty";
    this.formTarget.setAttribute('data-action', dirtyFormActions.trim());
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = this.messageValue;
    }

    var fields = this.formTarget.querySelectorAll(this.fieldSelectorValue);
    fields.forEach(field => {
      field.dataset.dirtyFormTarget = "field"
      field.dataset.initialField = "true";
    });
    this.origFieldCountValue = this.fieldTargets.length;
    this.isDirtyValue = false;
  }
  
  disconnect() {
    if (!this.hasFormTarget || this.formTarget.tagName.toLowerCase() !== 'form') {
      return;
    }
    var dirtyFormActions = this.formTarget.getAttribute('data-action') || '';
    dirtyFormActions = dirtyFormActions.replace(" submit->dirty-form#onFormSubmit reset->dirty-form#onFormReset dirtyForm.checkForm->dirty-form#checkForm dirtyForm.rescan->dirty-form#rescan dirtyForm.markDirty->dirty-form#markFormDirty", "");
    this.formTarget.setAttribute('data-action', dirtyFormActions.trim());
    this.removeUnloadEvents();
  }

  beforeUnloadHandler(e){
    if (this.silentValue || !this.isDirtyValue) {
      return false;
    }
    e.preventDefault();
    e.returnValue = this.messageValue;

    // Prevent multiple prompts - seen on Chrome and IE
    if (navigator.userAgent.toLowerCase().match(/msie|chrome/)) {
      if (window.dirtyHasPrompted) {
        return;
      }
      window.dirtyHasPrompted = true;
      window.setTimeout(function() {window.dirtyHasPrompted = false;}, 900);
    }

    return this.messageValue;
  }

  beforeUnloadHandlerTurbo(e){
    if (this.silentValue || !this.isDirtyValue || !this.hasModalTarget) { return; }
    e.preventDefault();
    this.destinationUrlValue = e.detail.url;
    $(this.modalTarget).modal('show');
  }

  addUnloadEvents() {
    window.addEventListener("beforeunload", this._beforeUnload);
    document.documentElement.addEventListener("turbo:before-visit", this._beforeUnloadTurbo);
  }

  removeUnloadEvents() {
    window.removeEventListener("beforeunload", this._beforeUnload);
    document.documentElement.removeEventListener("turbo:before-visit", this._beforeUnloadTurbo);
  }

  onFormSubmit() {
    this.isDirtyValue = false;
  }
  
  onFormReset() {
    this.isDirtyValue = false;
  }

  fieldTargetConnected(field) {
    this.storeOrigValue(field);
    this.addEventsToField(field);
    if (this.addRemoveFieldsMarksDirtyValue && field.dataset.initialField !== "true") {
      this.markFormDirty();
    }
  };

  fieldTargetDisconnected(field) {
    this.removeEventsFromField(field);
    if (this.addRemoveFieldsMarksDirtyValue) {
      this.markFormDirty();
    }
  }

  isDirtyValueChanged(){
    if (this.isDirtyValue) {
      this.addUnloadEvents();
    } else {
      this.removeUnloadEvents();
    }
    this.setDirtyAttributes();
  }

  setDirtyAttributes(){
    this.dirtyAttributesValue.split(',').map(attr => attr.trim()).forEach(attr => {
      
      if (this.isDirtyValue) {
        this.formTarget.dataset[attr] = "true";
      } else {
        delete this.formTarget.dataset[attr];
      }
    });
  }

  markFormDirty() {
    this.isDirtyValue = true;
    this.setDirtyAttributes();
  }

  getValue(field) {
    const name = field.getAttribute('name');

    if (field.classList.contains('dirty-ignore')
      || field.classList.contains('dirtyIgnore')
      || field.hasAttribute('data-dirty-ignore')
      || name === null
      || name === '') {
      return null;
    }

    if (field.disabled) {
      return 'dirty-disabled';
    }

    var val;
    var type = field.getAttribute('type');
    if (field.tagName.toLowerCase() === 'select') {
      type = 'select';
    }

    switch (type) {
      case 'checkbox':
      case 'radio':
        val = field.checked;
        break;
      case 'select':
        val = '';
        var options = field.options;
        for (var i = 0; i < options.length; i++) {
          var option = options[i];
          if (option.selected) {
            val += option.value;
          }
        }
        break;
      default:
        val = field.value;
    }

    return val;
  };

  storeOrigValue(field) {
    field.dataset.dirtyOrig = this.getValue(field);
  };

  isFieldDirty(field) {
    if (!field) return false;
    var origValue = field.dataset.dirtyOrig;
    if (undefined === origValue) {
      return false;
    }
    return (this.getValue(field) != origValue);
  };
  
  checkFormForChanges(target=null){
    if (this.isFieldDirty(target)) {
      this.markFormDirty();
      return;
    }

    if (this.addRemoveFieldsMarksDirtyValue) {              
      // Check if field count has changed
      var origCount = this.origFieldCountValue;
      if (origCount != this.fieldTargets.length) {
        this.markFormDirty();
        return;
      }
    }

    // Brute force - check each field
    var isDirty = false;
    this.fieldTargets.forEach(field => {
      if (this.isFieldDirty(field)) {
        isDirty = true;
        return false; // break
      }
    });
    this.isDirtyValue = isDirty;
  }
  
  checkForm(evt) {
    this.checkFormForChanges(evt ? evt.target : null);
  };

  rescan() {
    var fields = this.formTarget.querySelectorAll(this.fieldSelectorValue);
    fields.forEach( field => field.dataset.dirtyFormTarget = "field" );

    // Check for changes while we're here
    this.checkFormForChanges();
  };

  addEventsToField(field) {
    this.fieldEventsValue.forEach(event => { 
      field.addEventListener(event, this._checkForm);
    });
  }

  removeEventsFromField(field) {
    this.fieldEventsValue.forEach(event => { 
      field.removeEventListener(event, this._checkForm);
    });
  }

  confirmLeave(){
    this.isDirtyValue = false;
    this.setDirtyAttributes();
    $(this.modalTarget).modal('hide');
    if (this.destinationUrlValue) {
      Turbo.visit(this.destinationUrlValue);
    }
  }

}
